import React, { useState } from 'react';
import {
  Box,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import Masonry from 'react-masonry-css';

export interface GalleryImage {
  src: string;
  alt: string;
  title?: string;
}

interface GalleryProps {
  images: GalleryImage[];
  columns?: { base: number; md: number; lg: number };
  spacing?: number;
}

const Gallery: React.FC<GalleryProps> = ({
  images,
  columns = { base: 1, md: 2, lg: 3 },
  spacing = 4,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openModal = (index: number) => {
    setCurrentImageIndex(index);
    onOpen();
  };

  const navigateToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const navigateToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      navigateToNext();
    } else if (event.key === 'ArrowLeft') {
      navigateToPrevious();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      <Masonry
        breakpointCols={{
          default: columns.lg,
          768: columns.md,
          500: columns.base,
        }}
        className="masonry-grid"
        columnClassName="masonry-grid-column"
      >
        {images.map((image, index) => (
          <Box
            key={index}
            cursor="pointer"
            overflow="hidden"
            borderRadius="md"
            onClick={() => openModal(index)}
            mb={`${spacing}px`}
          >
            <Image
              src={image.src}
              alt={image.alt}
              w="100%"
              h="auto"
              objectFit="cover"
              loading="lazy"
            />
          </Box>
        ))}
      </Masonry>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent
          bg="white"
          maxW="90vw"
          maxH="90vh"
          minW={{ base: "95vw", md: "auto" }}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <ModalBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            p={{ base: 8, md: 4 }}
            w="100%"
            h="auto"
          >
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              position="absolute"
              top={2}
              right={2}
              colorScheme="blackAlpha"
              variant="ghost"
              size="sm"
              onClick={onClose}
              zIndex={2}
            />

            {images.length > 1 && (
              <IconButton
                aria-label="Previous image"
                icon={<ChevronLeftIcon />}
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                colorScheme="blackAlpha"
                variant="ghost"
                size={{ base: "md", md: "lg" }}
                onClick={navigateToPrevious}
                zIndex={2}
              />
            )}

            <Image
              src={images[currentImageIndex].src}
              alt={images[currentImageIndex].alt}
              maxW={images.length > 1 ? { base: "calc(100% - 80px)", md: "calc(100% - 100px)" } : "100%"}
              maxH="85vh"
              objectFit="contain"
              w="auto"
              h="auto"
            />

            {images.length > 1 && (
              <IconButton
                aria-label="Next image"
                icon={<ChevronRightIcon />}
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                colorScheme="blackAlpha"
                variant="ghost"
                size={{ base: "md", md: "lg" }}
                onClick={navigateToNext}
                zIndex={2}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Gallery;